import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useCurrency } from '../context/CurrencyContext';
import { useMonth } from '../context/MonthContext';
import { CURRENCY_INFO } from '../utils/currency';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, PiggyBank, AlertTriangle, CheckCircle, TrendingDown, Settings, Calendar, Info, Repeat } from 'lucide-react';

export const BudgetsPage = () => {
  const navigate = useNavigate();
  const { currency, formatCurrency } = useCurrency();
  const { selectedMonth, getMonthLabel } = useMonth();
  const [budgetStatus, setBudgetStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [existingBudget, setExistingBudget] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [spendingPreview, setSpendingPreview] = useState(0);
  
  // User's categories from Settings
  const [userCategories, setUserCategories] = useState([]);

  // Enhanced form data with period selection and notes
  const [formData, setFormData] = useState({
    category: 'total',
    limit_amount: '',
    currency: currency,
    month: selectedMonth,
    notes: '',
    applyEveryMonth: false
  });

  // Generate month options (current + next 12 months)
  const getMonthOptions = () => {
    const options = [];
    const today = new Date();
    for (let i = -3; i <= 12; i++) {
      const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleString('default', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options;
  };

  useEffect(() => {
    fetchBudgetStatus();
    fetchUserCategories();
  }, [selectedMonth]);

  useEffect(() => {
    setFormData(prev => ({ ...prev, currency, month: selectedMonth }));
  }, [currency, selectedMonth]);

  // Fetch spending preview when category or month changes
  useEffect(() => {
    if (dialogOpen) {
      fetchSpendingPreview();
    }
  }, [formData.category, formData.month, dialogOpen]);

  const fetchUserCategories = async () => {
    try {
      const response = await api.get('/categories');
      setUserCategories(response.data || []);
    } catch (error) {
      console.error('Failed to fetch user categories:', error);
    }
  };

  const fetchSpendingPreview = async () => {
    try {
      const categoryParam = formData.category === 'Total (All Expenses)' ? 'total' : formData.category;
      const response = await api.get('/budgets/spending-preview', {
        params: { month: formData.month, category: categoryParam }
      });
      setSpendingPreview(response.data.spent || 0);
    } catch (error) {
      console.error('Failed to fetch spending preview:', error);
      setSpendingPreview(0);
    }
  };

  // Only show "Total (All Expenses)" and user's own categories from Settings
  const allCategories = [
    'Total (All Expenses)',
    ...userCategories.filter(c => c.is_active).map(c => c.name)
  ];

  const fetchBudgetStatus = async () => {
    try {
      setLoading(true);
      const response = await api.get('/budgets/status', { params: { month: selectedMonth } });
      setBudgetStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch budget status:', error);
      setBudgetStatus({ budgets: [], alerts: [], total_spending: 0, spending_by_category: {} });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      category: 'total',
      limit_amount: '',
      currency: currency,
      month: selectedMonth,
      notes: '',
      applyEveryMonth: false
    });
    setEditingItem(null);
    setSpendingPreview(0);
  };

  const checkForDuplicate = async () => {
    if (editingItem) return null; // Skip check when editing
    
    try {
      const categoryParam = formData.category === 'Total (All Expenses)' ? 'total' : formData.category;
      const response = await api.get('/budgets/check-duplicate', {
        params: { month: formData.month, category: categoryParam }
      });
      return response.data.exists ? response.data.budget : null;
    } catch (error) {
      console.error('Failed to check duplicate:', error);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.limit_amount || parseFloat(formData.limit_amount) <= 0) {
      toast.error('Please enter a valid budget amount');
      return;
    }

    // Check for duplicate budget
    if (!editingItem) {
      const duplicate = await checkForDuplicate();
      if (duplicate) {
        setExistingBudget(duplicate);
        setDuplicateDialogOpen(true);
        return;
      }
    }

    await saveBudget();
  };

  const saveBudget = async (replaceExisting = false) => {
    try {
      setSaving(true);
      const payload = {
        category: formData.category === 'Total (All Expenses)' ? 'total' : formData.category,
        month: formData.month,
        limit_amount: parseFloat(formData.limit_amount),
        currency: formData.currency,
        notes: formData.notes || '',
        alert_threshold: 80 // Default threshold, customizable in settings
      };

      if (replaceExisting && existingBudget) {
        // Replace existing budget
        await api.put(`/budgets/${existingBudget.id}`, payload);
        toast.success('Budget replaced successfully');
      } else if (editingItem) {
        await api.put(`/budgets/${editingItem.id}`, payload);
        toast.success('Budget updated successfully');
      } else {
        await api.post('/budgets', payload);
        toast.success('Budget created successfully');
      }

      // If apply every month is enabled, create for future months
      if (formData.applyEveryMonth && !editingItem) {
        await createRecurringBudgets(payload);
      }
      
      setDialogOpen(false);
      setDuplicateDialogOpen(false);
      resetForm();
      fetchBudgetStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save budget');
    } finally {
      setSaving(false);
    }
  };

  const createRecurringBudgets = async (basePayload) => {
    // Create budgets for the next 11 months (total 12 including current)
    const [year, month] = formData.month.split('-').map(Number);
    let created = 0;

    for (let i = 1; i <= 11; i++) {
      const futureDate = new Date(year, month - 1 + i, 1);
      const futureMonth = `${futureDate.getFullYear()}-${String(futureDate.getMonth() + 1).padStart(2, '0')}`;
      
      try {
        await api.post('/budgets', { ...basePayload, month: futureMonth });
        created++;
      } catch (error) {
        // Skip if duplicate exists for that month
        console.log(`Budget already exists for ${futureMonth}`);
      }
    }

    if (created > 0) {
      toast.success(`Also created budgets for ${created} future months`);
    }
  };

  const handleEdit = (budget) => {
    setEditingItem(budget);
    setFormData({
      category: budget.category === 'total' ? 'Total (All Expenses)' : budget.category,
      limit_amount: budget.limit_amount.toString(),
      currency: budget.currency || currency,
      month: budget.month || selectedMonth,
      notes: budget.notes || '',
      applyEveryMonth: false
    });
    setDialogOpen(true);
  };

  const handleEditExisting = () => {
    setDuplicateDialogOpen(false);
    if (existingBudget) {
      handleEdit(existingBudget);
    }
  };

  const handleReplaceExisting = () => {
    setDuplicateDialogOpen(false);
    saveBudget(true);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/budgets/${itemToDelete.id}`);
      toast.success('Budget deleted');
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchBudgetStatus();
    } catch (error) {
      toast.error('Failed to delete budget');
    }
  };

  const getProgressColor = (percentage, isOver) => {
    if (isOver || percentage >= 100) return 'bg-red-500';
    if (percentage >= 90) return 'bg-orange-500';
    if (percentage >= 80) return 'bg-amber-500';
    return 'bg-emerald-500';
  };

  const projectedRemaining = parseFloat(formData.limit_amount || 0) - spendingPreview;

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Budget Limits
          </h1>
          <p className="text-slate-600 mt-1">Track spending against your budget for {getMonthLabel(selectedMonth)}</p>
        </div>

        <Button
          onClick={() => {
            resetForm();
            setDialogOpen(true);
          }}
          className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-xl px-6 py-2.5"
          data-testid="add-budget-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Set Budget Limit
        </Button>
      </div>

      {/* Summary Cards */}
      {budgetStatus && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-5 border border-blue-200">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-500 rounded-xl">
                <PiggyBank className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-blue-600 font-medium">Active Budgets</p>
                <p className="text-2xl font-bold text-blue-900">{budgetStatus.budgets?.length || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-5 border border-red-200">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-red-500 rounded-xl">
                <TrendingDown className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-red-600 font-medium">Total Spending</p>
                <p className="text-2xl font-bold text-red-900">{formatCurrency(budgetStatus.total_spending || 0, currency)}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-2xl p-5 border border-amber-200">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-amber-500 rounded-xl">
                <AlertTriangle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-amber-600 font-medium">Warnings</p>
                <p className="text-2xl font-bold text-amber-900">{budgetStatus.alerts?.filter(a => a.type === 'warning').length || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl p-5 border border-emerald-200">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-emerald-500 rounded-xl">
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-emerald-600 font-medium">On Track</p>
                <p className="text-2xl font-bold text-emerald-900">
                  {budgetStatus.budgets?.filter(b => (b.spent / b.limit_amount * 100) < 80).length || 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-12 h-12 border-4 border-slate-200 border-t-emerald-600 rounded-full animate-spin"></div>
        </div>
      ) : (
        <>
          {/* Budget Cards */}
          {budgetStatus?.budgets?.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mb-8">
              {budgetStatus.budgets.map((budget) => {
                const percentage = budget.limit_amount > 0 ? (budget.spent / budget.limit_amount) * 100 : 0;
                const isOver = budget.spent >= budget.limit_amount;
                
                return (
                  <div key={budget.id} className="bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-lg transition-shadow">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h4 className="font-semibold text-slate-900">{budget.category === 'total' ? 'All Expenses' : budget.category}</h4>
                        <p className="text-xs text-slate-500 mt-0.5">Budget for {getMonthLabel(budget.month)}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(budget)} data-testid={`edit-budget-${budget.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => { setItemToDelete(budget); setDeleteDialogOpen(true); }}
                          data-testid={`delete-budget-${budget.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <div className="flex justify-between text-sm mb-1.5">
                        <span className="text-slate-600">Spent: {formatCurrency(budget.spent, currency)}</span>
                        <span className={`font-semibold ${isOver ? 'text-red-600' : 'text-slate-900'}`}>
                          {Math.round(percentage)}%
                        </span>
                      </div>
                      <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${getProgressColor(percentage, isOver)}`}
                          style={{ width: `${Math.min(percentage, 100)}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-slate-500 mt-1.5">
                        <span>Limit: {formatCurrency(budget.limit_amount, currency)}</span>
                        <span className={isOver ? 'text-red-600 font-semibold' : ''}>
                          {isOver ? 'Over Budget!' : `${formatCurrency(budget.limit_amount - budget.spent, currency)} left`}
                        </span>
                      </div>
                    </div>

                    {budget.notes && (
                      <p className="text-xs text-slate-500 mt-2 italic border-t border-slate-100 pt-2">
                        {budget.notes}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center mb-8">
              <PiggyBank className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">No Budgets Set</h3>
              <p className="text-slate-500 mb-6">Start tracking your spending by creating your first budget limit</p>
              <Button
                onClick={() => setDialogOpen(true)}
                className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-xl"
              >
                <Plus className="w-4 h-4 mr-2" />
                Set Your First Budget
              </Button>
            </div>
          )}
        </>
      )}

      {/* Spending Summary - Only show categories with active budget limits */}
      {budgetStatus?.spending_by_category && budgetStatus?.budgets && budgetStatus.budgets.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Spending by Category</h3>
          {(() => {
            // Get list of categories with active budget limits
            // Backend uses 'category' field, and 'total' means all expenses
            const budgetedCategories = new Set(
              budgetStatus.budgets.map(b => b.category === 'total' ? 'Total (All Expenses)' : b.category)
            );
            // Filter spending to only show budgeted categories
            const filteredSpending = Object.entries(budgetStatus.spending_by_category)
              .filter(([category]) => budgetedCategories.has(category));
            
            if (filteredSpending.length === 0) {
              return (
                <p className="text-slate-500 text-center py-4">
                  No spending in budgeted categories yet
                </p>
              );
            }
            
            return (
              <>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {filteredSpending.map(([category, amount]) => (
                    <div key={category} className="flex justify-between items-center p-3 bg-slate-50 rounded-xl">
                      <span className="text-slate-700">{category}</span>
                      <span className="font-semibold text-slate-900">{formatCurrency(amount, currency)}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-slate-200 flex justify-between items-center">
                  <span className="text-lg font-medium text-slate-700">Budgeted Spending</span>
                  <span className="text-xl font-bold text-slate-900">
                    {formatCurrency(
                      filteredSpending.reduce((sum, [, amount]) => sum + amount, 0),
                      currency
                    )}
                  </span>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* Enhanced Add/Edit Dialog - Mobile Friendly & Scrollable */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-xl font-semibold">
              {editingItem ? 'Edit Budget Limit' : 'Set Budget Limit'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto space-y-4 pr-1 -mr-1">
              {/* Period Selection */}
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-4 h-4 text-emerald-600" />
                  <Label className="font-medium text-slate-700">Budget Period</Label>
                </div>
                <Select value={formData.month} onValueChange={(v) => setFormData({ ...formData, month: v })}>
                  <SelectTrigger className="bg-white" data-testid="budget-month-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {getMonthOptions().map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* Apply Every Month Toggle */}
                {!editingItem && (
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-200">
                    <div className="flex items-center gap-2">
                      <Repeat className="w-4 h-4 text-slate-500" />
                      <span className="text-sm text-slate-600">Apply every month</span>
                    </div>
                    <Switch
                      checked={formData.applyEveryMonth}
                      onCheckedChange={(checked) => setFormData({ ...formData, applyEveryMonth: checked })}
                      data-testid="apply-every-month-toggle"
                    />
                  </div>
                )}
              </div>

              {/* Category Selection */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="font-medium">Category</Label>
                  <button
                    type="button"
                    onClick={() => navigate('/settings')}
                    className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
                  >
                    <Settings className="w-3 h-3" />
                    Manage
                  </button>
                </div>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger data-testid="budget-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {allCategories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {userCategories.filter(c => c.is_active).length === 0 && (
                  <p className="text-xs text-amber-600 flex items-center gap-1">
                    <Info className="w-3 h-3" />
                    Add categories in Settings
                  </p>
                )}
              </div>

              {/* Budget Amount */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="font-medium">Amount</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.limit_amount}
                    onChange={(e) => setFormData({ ...formData, limit_amount: e.target.value })}
                    className="text-lg"
                    data-testid="budget-amount-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-medium">Currency</Label>
                  <Select value={formData.currency} onValueChange={(v) => setFormData({ ...formData, currency: v })}>
                    <SelectTrigger data-testid="budget-currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(CURRENCY_INFO).map(([code, info]) => (
                        <SelectItem key={code} value={code}>{info.symbol} {code}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Smart Preview */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-3 border border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <Info className="w-4 h-4 text-blue-600" />
                  <Label className="font-medium text-blue-800 text-sm">Preview</Label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-white/70 rounded-lg p-2.5">
                    <p className="text-xs text-slate-500">Spent</p>
                    <p className="text-base font-bold text-slate-900">
                      {formatCurrency(spendingPreview, formData.currency)}
                    </p>
                  </div>
                  <div className="bg-white/70 rounded-lg p-2.5">
                    <p className="text-xs text-slate-500">Remaining</p>
                    <p className={`text-base font-bold ${projectedRemaining >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formData.limit_amount 
                        ? formatCurrency(projectedRemaining, formData.currency)
                        : '—'
                      }
                    </p>
                  </div>
                </div>
                {projectedRemaining < 0 && formData.limit_amount && (
                  <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Already over limit!
                  </p>
                )}
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label className="font-medium">Notes (Optional)</Label>
                <Textarea
                  placeholder="Add context..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="resize-none h-16"
                  data-testid="budget-notes-input"
                />
              </div>
            </div>

            <DialogFooter className="flex-shrink-0 gap-2 pt-4 mt-4 border-t border-slate-100">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
                className="rounded-xl flex-1 sm:flex-none"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={saving || !formData.limit_amount} 
                className="bg-emerald-600 hover:bg-emerald-700 rounded-xl flex-1 sm:flex-none sm:px-6"
                data-testid="save-budget-button"
              >
                {saving ? 'Saving...' : (editingItem ? 'Update' : 'Create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Duplicate Budget Dialog */}
      <AlertDialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Budget Already Exists
            </AlertDialogTitle>
            <AlertDialogDescription>
              A budget for <strong>{formData.category}</strong> in <strong>{getMonthOptions().find(m => m.value === formData.month)?.label}</strong> already exists with a limit of {existingBudget && formatCurrency(existingBudget.limit_amount, existingBudget.currency)}.
              <br /><br />
              What would you like to do?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <Button 
              variant="outline" 
              onClick={handleEditExisting}
              className="rounded-xl"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit Existing
            </Button>
            <AlertDialogAction 
              onClick={handleReplaceExisting}
              className="bg-amber-600 hover:bg-amber-700 rounded-xl"
            >
              Replace Budget
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Budget?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this budget limit. You can create a new one anytime.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 rounded-xl">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
